import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ChevronLeft, ChevronRight, Mail, MoveRight, Phone } from "lucide-react";
import { MoveForm } from "./MoveForm";
import { Loading, PageHead, money, useAdmin } from "./ui";

const HOURS = Array.from({ length: 14 }, (_, i) => `${String(i + 7).padStart(2, "0")}:00`);
const HIDDEN = ["cancelled", "bumped"];
const todayStr = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/Nassau" });
const toDate = (s) => new Date(`${s}T12:00:00`);
const fmt = (d) => d.toLocaleDateString("en-CA");
const addDays = (s, n) => { const d = toDate(s); d.setDate(d.getDate() + n); return fmt(d); };
const weekOf = (s) => { const d = toDate(s); const start = addDays(s, -((d.getDay() + 6) % 7)); return Array.from({ length: 7 }, (_, i) => addDays(start, i)); };
const hourLabel = (t) => { const h = Number(t.slice(0, 2)); return `${((h - 1) % 12) + 1} ${h < 12 ? "AM" : "PM"}`; };

const CAN_MOVE = (s) => !["cancelled", "bumped"].includes(s);

const Assign = ({ b, team }) => {
  const qc = useQueryClient();
  const change = async (e) => {
    try { await api.patch(`/admin/bookings/${b.id}/staff`, { staff_id: e.target.value || null }); qc.invalidateQueries({ queryKey: ["admin", "bookings"] }); toast.success("Team member updated"); } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <label className="mu-field cal-assign">With
      <select value={b.staff_id || ""} onChange={change} data-testid="calendar-assign-select">
        <option value="">Unassigned</option>
        {team.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
      </select>
    </label>
  );
};

const Chip = ({ b, onMoved, team }) => {
  const [open, setOpen] = useState(false);
  const [moving, setMoving] = useState(false);
  const who = team.find((m) => m.id === b.staff_id);
  return (
    <div className={`cal-chip st-${b.status}`} style={who ? { borderLeft: `5px solid ${who.color}` } : undefined} data-testid={`calendar-booking-${b.id}`}>
      <button type="button" className="cal-chip-head" onClick={() => setOpen(!open)} aria-expanded={open} data-testid="calendar-booking-toggle">
        <strong>{b.name}</strong>
        <span className={`pill st-pill-${b.status}`}>{b.status.replace("_", " ")}</span>
        <small>{b.treatment.split(" — ").pop()}{b.duration && ` · ${b.duration}`}</small>
        {team.length > 0 && <small className="cal-who" data-testid="calendar-booking-staff"><span className="staff-dot" style={{ background: who?.color || "transparent", border: who ? 0 : "1px dashed #999" }} /> {who?.name || "Unassigned"}</small>}
      </button>
      {open && (
        <div className="cal-chip-body" data-testid="calendar-booking-details">
          {team.length > 0 && <Assign b={b} team={team} />}
          <p>{b.treatment}<br />{b.price}{b.deposit > 0 && ` · deposit ${money(b.deposit)} (${b.payment_status})`}</p>
          {b.notes && <p className="muted-sm">“{b.notes}”</p>}
          {b.moves?.length > 0 && <p className="muted-sm" data-testid="calendar-move-history">Moved from {b.moves[b.moves.length - 1].from}{b.moves.length > 1 && ` (${b.moves.length} moves)`}</p>}
          {moving ? <MoveForm b={b} onDone={(d) => { setMoving(false); if (d) onMoved(d); }} /> : (
          <div className="req-actions">
            {CAN_MOVE(b.status) && <button type="button" className="btn gold" onClick={() => setMoving(true)} data-testid="calendar-move-button"><MoveRight size={14} /> Move</button>}
            <a className="btn light" href={`tel:${b.phone}`} data-testid="calendar-call-button"><Phone size={14} /> Call</a>
            <a className="btn light" href={`mailto:${b.email}`} data-testid="calendar-email-button"><Mail size={14} /> Email</a>
          </div>
          )}
        </div>
      )}
    </div>
  );
};

const WeekStrip = ({ day, setDay, counts }) => (
  <div className="cal-week" data-testid="calendar-week">
    <button className="ad-icon-btn" onClick={() => setDay(addDays(day, -7))} aria-label="Previous week" data-testid="calendar-prev-week"><ChevronLeft size={16} /></button>
    {weekOf(day).map((d) => {
      const dt = toDate(d);
      return (
        <button key={d} className={`cal-day ${d === todayStr() ? "is-today" : ""}`} aria-pressed={d === day} onClick={() => setDay(d)} data-testid={`calendar-day-${d}`}>
          <small>{dt.toLocaleDateString("en-US", { weekday: "short" })}</small>
          <strong>{dt.getDate()}</strong>
          <span className="cal-count">{counts[d] || ""}</span>
        </button>
      );
    })}
    <button className="ad-icon-btn" onClick={() => setDay(addDays(day, 7))} aria-label="Next week" data-testid="calendar-next-week"><ChevronRight size={16} /></button>
  </div>
);

const Legend = ({ team, who, setWho }) => (
  <div className="cal-legend" data-testid="calendar-staff-legend">
    {[["all", "Everyone", ""], ...team.map((m) => [m.id, m.name, m.color]), ["none", "Unassigned", ""]].map(([id, name, color]) => (
      <button key={id} type="button" className="staff-opt" aria-checked={who === id} role="radio" onClick={() => setWho(id)} data-testid={`calendar-staff-filter-${id}`}>
        {id !== "all" && <span className="staff-dot" style={{ background: color || "transparent", border: color ? 0 : "1px dashed #999" }} />}{name}
      </button>
    ))}
  </div>
);

export default function Calendar() {
  const { data } = useAdmin("bookings", "/admin/bookings");
  const { data: team = [] } = useQuery({ queryKey: ["team-staff"], queryFn: () => api.get("/team/staff").then((r) => r.data) });
  const [who, setWho] = useState("all");
  const [day, setDay] = useState(todayStr());
  const [showAll, setShowAll] = useState(false);
  const visible = useMemo(() => (data || []).filter((b) => showAll || !HIDDEN.includes(b.status)), [data, showAll]);
  const counts = useMemo(() => visible.reduce((m, b) => ({ ...m, [b.preferred_date]: (m[b.preferred_date] || 0) + 1 }), {}), [visible]);
  const dayList = visible.filter((b) => b.preferred_date === day && (who === "all" || (who === "none" ? !b.staff_id : b.staff_id === who)));
  const confirmed = dayList.filter((b) => b.status === "confirmed" || b.status === "completed").length;

  return (
    <div data-testid="admin-calendar">
      <PageHead eyebrow="Schedule" title="Calendar">
        <input type="date" className="cal-date" value={day} onChange={(e) => e.target.value && setDay(e.target.value)} data-testid="calendar-date-input" />
        <button className="btn light" onClick={() => setDay(todayStr())} data-testid="calendar-today-button">Today</button>
      </PageHead>
      <WeekStrip day={day} setDay={setDay} counts={counts} />
      {team.length > 0 && <Legend team={team} who={who} setWho={setWho} />}
      <div className="cal-summary">
        <h2 data-testid="calendar-day-title">{toDate(day).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}</h2>
        <p className="muted-sm" data-testid="calendar-day-summary">{dayList.length} booking{dayList.length === 1 ? "" : "s"} · {confirmed} confirmed · {dayList.length - confirmed} tentative / pending</p>
        <label className="ad-check"><input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} data-testid="calendar-show-cancelled" /> Show cancelled &amp; bumped</label>
      </div>
      {!data ? <Loading /> : (
        <div className="cal-grid" data-testid="calendar-timeline">
          {HOURS.map((h) => {
            const items = dayList.filter((b) => b.time_slot === h);
            return (
              <div className={`cal-row ${items.length ? "has" : ""}`} key={h} data-testid={`calendar-row-${h}`}>
                <span className="cal-time">{hourLabel(h)}</span>
                <div className="cal-items">{items.length ? items.map((b) => <Chip key={b.id} b={b} onMoved={setDay} team={team} />) : <span className="cal-free">Open</span>}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
